import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  LayoutDashboard, Package, Users, ShoppingCart, DollarSign, 
  CheckCircle, XCircle, Star, Clock, TrendingUp, Eye, ChevronDown,
  Percent, ThumbsUp, AlertCircle, CreditCard, UserPlus, Building, MapPin, Phone,
  Ban, RotateCcw, Trash2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Header, Footer } from "./LandingPage";
import { useAuth, API, ADMIN_PORTAL_PATH } from "../App";
import { toast } from "sonner";
import axios from "axios";

const AdminDashboard = () => {
  const { token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [pendingVendors, setPendingVendors] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorAnalytics, setVendorAnalytics] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [votingStats, setVotingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorProducts, setVendorProducts] = useState([]);
  const [selectedOnboarding, setSelectedOnboarding] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [dashboardRes, pendingRes, vendorsRes, ordersRes, customersRes, vendorAnalyticsRes, votingRes, pendingVendorsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/products/pending`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/vendors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/analytics/vendors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/voting-stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/vendors/pending`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDashboard(dashboardRes.data);
      setPendingProducts(pendingRes.data);
      setVendors(vendorsRes.data);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setVendorAnalytics(vendorAnalyticsRes.data);
      setVotingStats(votingRes.data);
      setPendingVendors(pendingVendorsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorProducts = async (vendorId) => {
    try {
      const res = await axios.get(`${API}/admin/vendors/${vendorId}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorProducts(res.data);
      setSelectedVendor(vendorId);
    } catch (error) {
      toast.error("Failed to fetch vendor products");
    }
  };

  const handleApproveProduct = async (productId, status) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/approve`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Product ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const handleToggleFeatured = async (productId, featured) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/featured`, { featured }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(featured ? "Product featured" : "Product unfeatured");
      fetchData();
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?order_status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Order status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update order");
    }
  };

  const handleApproveVendor = async (userId, approved, rejectionReason = null) => {
    try {
      await axios.put(`${API}/admin/vendors/${userId}/approve?approved=${approved}${rejectionReason ? `&rejection_reason=${encodeURIComponent(rejectionReason)}` : ''}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Vendor ${approved ? 'approved' : 'rejected'} successfully`);
      setSelectedOnboarding(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update vendor status");
    }
  };

  const handleToggleVendorStatus = async (vendor) => {
    const nextActive = !vendor.is_active;
    const action = nextActive ? "restore" : "suspend";
    const message = nextActive
      ? `Restore ${vendor.brand_name || vendor.name}? Their previously listed jerseys will return to their prior status.`
      : `Suspend ${vendor.brand_name || vendor.name}? All jerseys from this vendor will be removed from the website.`;

    if (!window.confirm(message)) {
      return;
    }

    try {
      const response = await axios.put(`${API}/admin/vendors/${vendor.vendor_id}/status?is_active=${nextActive}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const productCount = nextActive ? response.data.restored_products : response.data.hidden_products;
      toast.success(`Vendor ${nextActive ? "restored" : "suspended"}. ${productCount || 0} jersey listings updated.`);
      if (selectedVendor === vendor.vendor_id) {
        await fetchVendorProducts(vendor.vendor_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} vendor`);
    }
  };

  const handleDeleteVendor = async (vendor) => {
    const vendorLabel = vendor.brand_name || vendor.name || vendor.email;
    if (!window.confirm(`Delete ${vendorLabel}? This removes the vendor account and takes all their jerseys off the website. This cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/admin/vendors/${vendor.vendor_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Vendor deleted. ${response.data.hidden_products || 0} jersey listings removed from the website.`);
      if (selectedVendor === vendor.vendor_id) {
        setSelectedVendor(null);
        setVendorProducts([]);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete vendor");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid": return "text-ghana-green bg-ghana-green/10";
      case "pending": return "text-ashanti-gold bg-ashanti-gold/10";
      case "failed": return "text-ghana-red bg-ghana-red/10";
      case "order_placed": return "text-slate-700 bg-slate-100";
      case "processing": return "text-blue-600 bg-blue-100";
      case "shipped": return "text-purple-600 bg-purple-100";
      case "delivered": return "text-ghana-green bg-ghana-green/10";
      case "cancelled": return "text-ghana-red bg-ghana-red/10";
      default: return "text-muted-text bg-gray-100";
    }
  };

  const formatOrderStep = (status) => {
    switch (status) {
      case "order_placed": return "Order Placed";
      case "processing": return "Processing";
      case "shipped": return "Shipped";
      case "delivered": return "Delivered";
      case "cancelled": return "Cancelled";
      default: return status || "Unknown";
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleString();
  };

  const formatCurrencyBreakdown = (breakdown, fallbackValue, fallbackCurrency = "USD") => {
    if (breakdown && Object.keys(breakdown).length > 0) {
      return Object.entries(breakdown)
        .sort(([currencyA], [currencyB]) => currencyA.localeCompare(currencyB))
        .map(([currency, amount]) => `${currency} ${Number(amount || 0).toFixed(2)}`)
        .join(" • ");
    }
    return `${fallbackCurrency} ${Number(fallbackValue || 0).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone-white">
        <Header forceLight={true} stickyAnnouncement={true} />
        <div className="pt-12 flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse">
            <div className="w-12 h-12 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-white" data-testid="admin-dashboard">
      <Header forceLight={true} stickyAnnouncement={true} />

      <div className="pt-12 pb-24 px-6 md:px-12 max-w-7xl mx-auto">
        {/* Title */}
        <div className="mb-12">
          <h1 className="font-heading text-2xl md:text-3xl tracking-widest uppercase" data-testid="admin-title">
            Admin Dashboard
          </h1>
          <p className="font-body text-muted-text mt-2">Manage your marketplace</p>
          <div className="mt-4">
            <Link to={`${ADMIN_PORTAL_PATH}/blog`}>
              <Button variant="outline" className="border-black">Open Blog Editor</Button>
            </Link>
          </div>
        </div>

        {/* Revenue Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-black text-white p-6 border border-black">
            <div className="flex items-center gap-2 text-white/60 mb-2">
              <DollarSign size={16} />
              <span className="font-body text-xs uppercase tracking-wider">Total Revenue</span>
            </div>
            <span className="font-body text-2xl font-medium">{formatCurrencyBreakdown(dashboard?.revenue_breakdown, dashboard?.total_revenue)}</span>
          </div>
          <div className="bg-ashanti-gold p-6 border border-ashanti-gold">
            <div className="flex items-center gap-2 text-black/60 mb-2">
              <Percent size={16} />
              <span className="font-body text-xs uppercase tracking-wider">Platform Commission (15%)</span>
            </div>
            <span className="font-body text-2xl font-medium">{formatCurrencyBreakdown(dashboard?.platform_commission_breakdown, dashboard?.platform_commission)}</span>
          </div>
          <div className="bg-white p-6 border border-black/10">
            <div className="flex items-center gap-2 text-muted-text mb-2">
              <CreditCard size={16} />
              <span className="font-body text-xs uppercase tracking-wider">Vendor Earnings</span>
            </div>
            <span className="font-body text-2xl font-medium">{formatCurrencyBreakdown(dashboard?.vendor_earnings_breakdown, dashboard?.vendor_earnings_total)}</span>
          </div>
          <div className="bg-white p-6 border border-black/10">
            <div className="flex items-center gap-2 text-muted-text mb-2">
              <CheckCircle size={16} />
              <span className="font-body text-xs uppercase tracking-wider">Confirmed Deliveries</span>
            </div>
            <span className="font-body text-2xl font-medium">{dashboard?.confirmed_deliveries || 0}</span>
            {dashboard?.pending_confirmations > 0 && (
              <span className="text-xs text-ashanti-gold ml-2">({dashboard.pending_confirmations} pending)</span>
            )}
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-12">
          <div className="bg-white p-4 border border-black/10">
            <div className="flex items-center gap-2 text-muted-text mb-1">
              <ShoppingCart size={14} />
              <span className="font-body text-xs uppercase tracking-wider">Orders</span>
            </div>
            <span className="font-body text-xl font-medium">{dashboard?.total_orders}</span>
          </div>
          <div className="bg-white p-4 border border-black/10">
            <div className="flex items-center gap-2 text-muted-text mb-1">
              <Package size={14} />
              <span className="font-body text-xs uppercase tracking-wider">Products</span>
            </div>
            <span className="font-body text-xl font-medium">{dashboard?.total_products}</span>
          </div>
          <div className="bg-white p-4 border border-black/10">
            <div className="flex items-center gap-2 text-ashanti-gold mb-1">
              <Clock size={14} />
              <span className="font-body text-xs uppercase tracking-wider">Pending</span>
            </div>
            <span className="font-body text-xl font-medium text-ashanti-gold">{dashboard?.pending_products}</span>
          </div>
          <div className="bg-white p-4 border border-black/10">
            <div className="flex items-center gap-2 text-muted-text mb-1">
              <Users size={14} />
              <span className="font-body text-xs uppercase tracking-wider">Vendors</span>
            </div>
            <span className="font-body text-xl font-medium">{dashboard?.total_vendors}</span>
          </div>
          <div className="bg-white p-4 border border-black/10">
            <div className="flex items-center gap-2 text-muted-text mb-1">
              <Users size={14} />
              <span className="font-body text-xs uppercase tracking-wider">Customers</span>
            </div>
            <span className="font-body text-xl font-medium">{dashboard?.total_customers}</span>
          </div>
          <div className="bg-white p-4 border border-black/10">
            <div className="flex items-center gap-2 text-muted-text mb-1">
              <ThumbsUp size={14} />
              <span className="font-body text-xs uppercase tracking-wider">Total Votes</span>
            </div>
            <span className="font-body text-xl font-medium">{votingStats?.total_votes || 0}</span>
          </div>
        </div>

        <Tabs defaultValue="onboarding" className="w-full">
          <TabsList className="bg-transparent border-b border-black/10 w-full justify-start rounded-none h-auto p-0 mb-8 overflow-x-auto flex-nowrap">
            <TabsTrigger 
              value="onboarding" 
              className="font-body text-sm uppercase tracking-widest rounded-none data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:bg-transparent py-4 px-4 whitespace-nowrap"
            >
              Onboarding {pendingVendors.length > 0 && <span className="ml-1 bg-ghana-red text-white text-xs px-2 py-0.5 rounded-full">{pendingVendors.length}</span>}
            </TabsTrigger>
            <TabsTrigger 
              value="vendors" 
              className="font-body text-sm uppercase tracking-widest rounded-none data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:bg-transparent py-4 px-4 whitespace-nowrap"
            >
              Vendors & Earnings
            </TabsTrigger>
            <TabsTrigger 
              value="pending" 
              className="font-body text-sm uppercase tracking-widest rounded-none data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:bg-transparent py-4 px-4 whitespace-nowrap"
            >
              Pending ({pendingProducts.length})
            </TabsTrigger>
            <TabsTrigger 
              value="orders" 
              className="font-body text-sm uppercase tracking-widest rounded-none data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:bg-transparent py-4 px-4 whitespace-nowrap"
            >
              Orders
            </TabsTrigger>
            <TabsTrigger 
              value="voting" 
              className="font-body text-sm uppercase tracking-widest rounded-none data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:bg-transparent py-4 px-4 whitespace-nowrap"
            >
              Voting Panel
            </TabsTrigger>
            <TabsTrigger 
              value="customers" 
              className="font-body text-sm uppercase tracking-widest rounded-none data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:bg-transparent py-4 px-4 whitespace-nowrap"
            >
              Customers
            </TabsTrigger>
          </TabsList>

          {/* Vendor Onboarding Applications Tab */}
          <TabsContent value="onboarding">
            <div className="space-y-6">
              <div className="bg-ashanti-gold/10 border border-ashanti-gold/30 p-4 mb-6">
                <h3 className="font-heading text-lg mb-2 flex items-center gap-2">
                  <UserPlus size={20} /> Vendor Onboarding Applications
                </h3>
                <p className="font-body text-sm text-muted-text">
                  Review and approve new vendor applications. Ensure they meet quality standards before granting access to the platform.
                </p>
              </div>

              {pendingVendors.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle size={48} className="mx-auto text-ghana-green mb-4" />
                  <p className="font-body text-muted-text">No pending vendor applications</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {pendingVendors.map((vendor) => (
                    <div key={vendor.user_id} className="bg-white border border-black/10 overflow-hidden" data-testid={`vendor-application-${vendor.user_id}`}>
                      <div className="p-4 bg-black text-white">
                        <h3 className="font-heading text-lg">{vendor.onboarding_data?.identity?.business_name || vendor.name}</h3>
                        <p className="font-body text-xs text-white/70">{vendor.email}</p>
                      </div>
                      <div className="p-4 space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Building size={14} className="text-muted-text" />
                          <span className="font-body">{vendor.onboarding_data?.identity?.full_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin size={14} className="text-muted-text" />
                          <span className="font-body">{vendor.onboarding_data?.identity?.city_location || "Not specified"}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone size={14} className="text-muted-text" />
                          <span className="font-body">{vendor.onboarding_data?.identity?.phone_number || "Not specified"}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-body text-muted-text">Experience: </span>
                          <span className="font-body capitalize">{vendor.onboarding_data?.identity?.years_in_business?.replace(/_/g, " ") || "Unknown"}</span>
                        </div>
                        <div className="text-sm">
                          <span className="font-body text-muted-text">Monthly Sales: </span>
                          <span className="font-body capitalize">{vendor.onboarding_data?.business?.jerseys_per_month?.replace(/_/g, " ") || "Unknown"}</span>
                        </div>
                      </div>
                      <div className="p-4 border-t border-black/10 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-black"
                          onClick={() => setSelectedOnboarding(vendor)}
                        >
                          <Eye size={14} className="mr-1" /> Review
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-ghana-green hover:bg-ghana-green/80"
                          onClick={() => handleApproveVendor(vendor.user_id, true)}
                        >
                          <CheckCircle size={14} className="mr-1" /> Approve
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Vendors & Earnings Tab */}
          <TabsContent value="vendors">
            <div className="space-y-6">
              {vendorAnalytics.map((vendor) => (
                <div key={vendor.vendor_id} className="bg-white border border-black/10 p-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-heading text-lg">{vendor.brand_name || vendor.name}</h3>
                      <p className="font-body text-sm text-muted-text">{vendor.email}</p>
                      <span className={`inline-block mt-2 px-2 py-1 text-xs font-body capitalize ${
                        vendor.vendor_status === "suspended" || !vendor.is_active
                          ? 'bg-ghana-red/10 text-ghana-red'
                          : vendor.vendor_status === "approved"
                            ? 'bg-ghana-green/10 text-ghana-green'
                            : 'bg-ashanti-gold/10 text-ashanti-gold'
                      }`}>
                        {vendor.vendor_status?.replace(/_/g, " ") || (vendor.is_active ? 'Active' : 'Inactive')}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-black"
                        onClick={() => fetchVendorProducts(vendor.vendor_id)}
                      >
                        <Eye size={14} className="mr-1" />
                        Products ({vendor.total_products})
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className={vendor.is_active ? "border-ashanti-gold text-ashanti-gold" : "border-ghana-green text-ghana-green"}
                        onClick={() => handleToggleVendorStatus(vendor)}
                      >
                        {vendor.is_active ? <Ban size={14} className="mr-1" /> : <RotateCcw size={14} className="mr-1" />}
                        {vendor.is_active ? "Suspend" : "Restore"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-ghana-red text-ghana-red hover:bg-ghana-red hover:text-white"
                        onClick={() => handleDeleteVendor(vendor)}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <p className="font-body text-xs text-muted-text uppercase tracking-wider">Total Revenue</p>
                      <p className="font-body text-lg font-medium">{formatCurrencyBreakdown(vendor.revenue_breakdown, vendor.total_revenue)}</p>
                    </div>
                    <div>
                      <p className="font-body text-xs text-muted-text uppercase tracking-wider">Platform Fee (15%)</p>
                      <p className="font-body text-lg font-medium text-ashanti-gold">{formatCurrencyBreakdown(vendor.platform_commission_breakdown, vendor.platform_commission)}</p>
                    </div>
                    <div>
                      <p className="font-body text-xs text-muted-text uppercase tracking-wider">Net Earnings</p>
                      <p className="font-body text-lg font-medium text-ghana-green">{formatCurrencyBreakdown(vendor.net_earnings_breakdown, vendor.net_earnings)}</p>
                    </div>
                    <div>
                      <p className="font-body text-xs text-muted-text uppercase tracking-wider">Pending Payout</p>
                      <p className="font-body text-lg font-medium">{formatCurrencyBreakdown(vendor.pending_payout_breakdown, vendor.pending_payout)}</p>
                    </div>
                    <div>
                      <p className="font-body text-xs text-muted-text uppercase tracking-wider">Paid Out</p>
                      <p className="font-body text-lg font-medium">{formatCurrencyBreakdown(vendor.paid_payout_breakdown, vendor.paid_payout)}</p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-black/10 grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="font-body text-xs text-muted-text">Products</p>
                      <p className="font-body font-medium">{vendor.approved_products} / {vendor.total_products}</p>
                    </div>
                    <div>
                      <p className="font-body text-xs text-muted-text">Orders</p>
                      <p className="font-body font-medium">{vendor.total_orders}</p>
                    </div>
                    <div>
                      <p className="font-body text-xs text-muted-text">Pending Approval</p>
                      <p className="font-body font-medium text-ashanti-gold">{vendor.pending_products}</p>
                    </div>
                    <div>
                      <p className="font-body text-xs text-muted-text">Votes</p>
                      <p className="font-body font-medium">{vendor.total_votes}</p>
                    </div>
                  </div>

                  {/* Vendor Products Dropdown */}
                  {selectedVendor === vendor.vendor_id && vendorProducts.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-black/10">
                      <h4 className="font-body text-sm font-semibold mb-3">Products by {vendor.brand_name || vendor.name}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {vendorProducts.map((product) => (
                          <div key={product.product_id} className="flex items-center gap-3 p-3 bg-bone-white">
                            <img src={product.images?.[0]} alt={product.name} className="w-16 h-16 object-cover" />
                            <div className="flex-1 min-w-0">
                              <p className="font-body text-sm font-medium truncate">{product.name}</p>
                              <p className="font-body text-xs text-muted-text">USD {Number(product.price || 0).toFixed(2)} • GHS {Number(product.price_ghs || 0).toFixed(2)} • {product.stock} in stock</p>
                              <span className={`text-xs ${
                                product.status === 'approved'
                                  ? 'text-ghana-green'
                                  : product.status === 'vendor_suspended' || product.status === 'vendor_deleted'
                                    ? 'text-ghana-red'
                                    : 'text-ashanti-gold'
                              }`}>
                                {product.status?.replace(/_/g, " ")}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {vendorAnalytics.length === 0 && (
                <p className="text-center text-muted-text py-12 font-body">No vendors registered yet</p>
              )}
            </div>
          </TabsContent>

          {/* Pending Products */}
          <TabsContent value="pending">
            {pendingProducts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pendingProducts.map((product) => (
                  <div key={product.product_id} className="bg-white border border-black/10 overflow-hidden">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={product.images?.[0] || "https://via.placeholder.com/400"}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm tracking-wide mb-1">{product.name}</h3>
                      <p className="font-body text-xs text-muted-text mb-2">by {product.vendor_name}</p>
                      <p className="font-body text-lg font-semibold mb-4">${product.price}</p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 bg-ghana-green hover:bg-ghana-green/80"
                          onClick={() => handleApproveProduct(product.product_id, "approved")}
                        >
                          <CheckCircle size={14} className="mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-ghana-red text-ghana-red hover:bg-ghana-red hover:text-white"
                          onClick={() => handleApproveProduct(product.product_id, "rejected")}
                        >
                          <XCircle size={14} className="mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-text py-12 font-body">No pending products</p>
            )}
          </TabsContent>

          {/* Orders */}
          <TabsContent value="orders">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                    <tr className="border-b border-black/10">
                      <th className="text-left font-body text-xs uppercase tracking-wider py-4 px-4">Order ID</th>
                      <th className="text-left font-body text-xs uppercase tracking-wider py-4 px-4">Customer</th>
                      <th className="text-left font-body text-xs uppercase tracking-wider py-4 px-4">Items & Vendor Progress</th>
                      <th className="text-left font-body text-xs uppercase tracking-wider py-4 px-4">Total</th>
                      <th className="text-left font-body text-xs uppercase tracking-wider py-4 px-4">Payment</th>
                      <th className="text-left font-body text-xs uppercase tracking-wider py-4 px-4">Confirmed</th>
                    <th className="text-left font-body text-xs uppercase tracking-wider py-4 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.order_id} className="border-b border-black/5">
                      <td className="py-4 px-4 font-body text-sm">{order.order_id.slice(-8)}</td>
                      <td className="py-4 px-4">
                        <p className="font-body text-sm">{order.shipping_address?.full_name || order.shipping_address?.name || "N/A"}</p>
                        <p className="font-body text-xs text-muted-text">{order.shipping_address?.phone}</p>
                        <p className="font-body text-xs text-muted-text mt-1">{formatDateTime(order.created_at)}</p>
                      </td>
                      <td className="py-4 px-4">
                        <div className="space-y-3 min-w-[320px]">
                          {order.items?.map((item, index) => (
                            <div key={`${order.order_id}-${item.product_id}-${index}`} className="border border-black/10 bg-white p-3">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-body text-sm font-medium">{item.name}</p>
                                  <p className="font-body text-xs text-muted-text">
                                    {item.vendor_name} • Size {item.size} • Qty {item.quantity}
                                  </p>
                                </div>
                                <span className={`inline-block px-2 py-1 text-xs font-body whitespace-nowrap ${getStatusColor(item.processing_status)}`}>
                                  {formatOrderStep(item.processing_status)}
                                </span>
                              </div>

                              <div className="mt-3">
                                <p className="font-body text-[11px] uppercase tracking-wider text-muted-text mb-2">Processing Timeline</p>
                                <div className="space-y-2">
                                  {(item.processing_history?.length ? item.processing_history : [{ status: "order_placed", timestamp: order.created_at }]).map((step, stepIndex) => (
                                    <div key={`${order.order_id}-${item.product_id}-${stepIndex}`} className="flex items-center justify-between gap-3 text-xs">
                                      <span className={`inline-block px-2 py-1 font-body ${getStatusColor(step.status)}`}>
                                        {formatOrderStep(step.status)}
                                      </span>
                                      <span className="font-body text-muted-text">{formatDateTime(step.timestamp)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="py-4 px-4 font-body text-sm font-medium">{`${order.currency || "USD"} ${Number(order.total || 0).toFixed(2)}`}</td>
                      <td className="py-4 px-4">
                        <span className={`inline-block px-2 py-1 text-xs font-body ${getStatusColor(order.payment_status)}`}>
                          {order.payment_status}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {order.delivery_confirmed ? (
                          <span className="text-ghana-green text-xs flex items-center gap-1">
                            <CheckCircle size={12} /> Yes
                          </span>
                        ) : (
                          <span className="text-muted-text text-xs flex items-center gap-1">
                            <Clock size={12} /> No
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <Select
                          value={order.order_status}
                          onValueChange={(value) => handleUpdateOrderStatus(order.order_id, value)}
                        >
                          <SelectTrigger className="w-32 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="processing">Processing</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orders.length === 0 && (
                <p className="text-center text-muted-text py-12 font-body">No orders yet</p>
              )}
            </div>
          </TabsContent>

          {/* Voting Panel (View Only) */}
          <TabsContent value="voting">
            <div className="bg-white border border-black/10 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-lg">Jersey Voting Statistics</h3>
                  <p className="font-body text-sm text-muted-text">View-only panel - votes determined by customers</p>
                </div>
                <div className="text-center">
                  <p className="font-body text-3xl font-bold text-ashanti-gold">{votingStats?.total_votes || 0}</p>
                  <p className="font-body text-xs text-muted-text uppercase">Total Votes</p>
                </div>
              </div>
              
              {votingStats?.top_voted && (
                <div className="bg-ashanti-gold/10 border border-ashanti-gold/30 p-4 flex items-center gap-4">
                  <img 
                    src={votingStats.top_voted.images?.[0]} 
                    alt={votingStats.top_voted.name}
                    className="w-20 h-20 object-cover"
                  />
                  <div className="flex-1">
                    <p className="font-body text-xs text-ashanti-gold uppercase tracking-wider mb-1">Current Leader</p>
                    <h4 className="font-heading text-lg">{votingStats.top_voted.name}</h4>
                    <p className="font-body text-sm text-muted-text">by {votingStats.top_voted.vendor_name}</p>
                  </div>
                  <div className="text-center">
                    <p className="font-body text-2xl font-bold">{votingStats.top_voted.vote_count}</p>
                    <p className="font-body text-xs text-muted-text">votes</p>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {votingStats?.products?.map((product, index) => (
                <div key={product.product_id} className="bg-white border border-black/10 p-4 flex items-center gap-4">
                  <div className="font-heading text-2xl text-muted-text w-8">#{index + 1}</div>
                  <img 
                    src={product.images?.[0]} 
                    alt={product.name}
                    className="w-16 h-16 object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-body text-sm font-medium truncate">{product.name}</h4>
                    <p className="font-body text-xs text-muted-text">{product.vendor_name}</p>
                  </div>
                  <div className="text-center">
                    <p className="font-body text-lg font-bold">{product.vote_count || 0}</p>
                    <p className="font-body text-xs text-muted-text">votes</p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Customers */}
          <TabsContent value="customers">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-black/10">
                    <th className="text-left font-body text-xs uppercase tracking-wider py-4 px-4">Name</th>
                    <th className="text-left font-body text-xs uppercase tracking-wider py-4 px-4">Email</th>
                    <th className="text-left font-body text-xs uppercase tracking-wider py-4 px-4">Joined</th>
                    <th className="text-left font-body text-xs uppercase tracking-wider py-4 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.user_id} className="border-b border-black/5">
                      <td className="py-4 px-4 font-body text-sm">{customer.name}</td>
                      <td className="py-4 px-4 font-body text-sm text-muted-text">{customer.email}</td>
                      <td className="py-4 px-4 font-body text-sm text-muted-text">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-block px-2 py-1 text-xs font-body ${customer.is_active ? 'bg-ghana-green/10 text-ghana-green' : 'bg-gray-100 text-muted-text'}`}>
                          {customer.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {customers.length === 0 && (
                <p className="text-center text-muted-text py-12 font-body">No customers yet</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Vendor Onboarding Detail Modal */}
      <Dialog open={!!selectedOnboarding} onOpenChange={() => setSelectedOnboarding(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-widest uppercase">
              Vendor Application Review
            </DialogTitle>
          </DialogHeader>
          
          {selectedOnboarding && (
            <div className="space-y-6 mt-4">
              {/* Identity Section */}
              <div className="border border-black/10 p-4">
                <h3 className="font-heading text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Building size={16} /> Vendor Identity
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-text">Full Name:</span>
                    <p className="font-medium">{selectedOnboarding.onboarding_data?.identity?.full_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-text">Business Name:</span>
                    <p className="font-medium">{selectedOnboarding.onboarding_data?.identity?.business_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-text">Phone:</span>
                    <p className="font-medium">{selectedOnboarding.onboarding_data?.identity?.phone_number}</p>
                  </div>
                  <div>
                    <span className="text-muted-text">Email:</span>
                    <p className="font-medium">{selectedOnboarding.onboarding_data?.identity?.email}</p>
                  </div>
                  <div>
                    <span className="text-muted-text">Location:</span>
                    <p className="font-medium">{selectedOnboarding.onboarding_data?.identity?.city_location}</p>
                  </div>
                  <div>
                    <span className="text-muted-text">Experience:</span>
                    <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.identity?.years_in_business?.replace(/_/g, " ")}</p>
                  </div>
                </div>
                {selectedOnboarding.onboarding_data?.identity?.social_handles?.length > 0 && (
                  <div className="mt-3">
                    <span className="text-muted-text text-sm">Social Media:</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedOnboarding.onboarding_data.identity.social_handles.map((h, i) => (
                        <span key={i} className="bg-black/5 px-2 py-1 text-xs">
                          {h.platform}: {h.handle}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Business Section */}
              <div className="border border-black/10 p-4">
                <h3 className="font-heading text-sm uppercase tracking-wider mb-3">Business Legitimacy</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-text">Selling Mode:</span>
                    <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.business?.sells_online_offline}</p>
                  </div>
                  <div>
                    <span className="text-muted-text">Monthly Sales:</span>
                    <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.business?.jerseys_per_month?.replace(/_/g, " ")}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-muted-text">Platforms:</span>
                    <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.business?.selling_platforms?.join(", ")?.replace(/_/g, " ")}</p>
                  </div>
                </div>
              </div>

              {/* Inventory & Production */}
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-black/10 p-4">
                  <h3 className="font-heading text-sm uppercase tracking-wider mb-3">Inventory</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-muted-text">Stock Type:</span>
                      <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.inventory?.keeps_stock?.replace(/_/g, " ")}</p>
                    </div>
                    <div>
                      <span className="text-muted-text">Stock Quantity:</span>
                      <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.inventory?.stock_quantity?.replace(/_/g, " ")}</p>
                    </div>
                    <div>
                      <span className="text-muted-text">Sizes:</span>
                      <p className="font-medium">{selectedOnboarding.onboarding_data?.inventory?.stock_sizes?.join(", ")}</p>
                    </div>
                  </div>
                </div>
                <div className="border border-black/10 p-4">
                  <h3 className="font-heading text-sm uppercase tracking-wider mb-3">Production</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-muted-text">Weekly Capacity:</span>
                      <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.production?.weekly_capacity?.replace(/_/g, " ")}</p>
                    </div>
                    <div>
                      <span className="text-muted-text">Production Time:</span>
                      <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.production?.production_time?.replace(/_/g, " ")}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery */}
              <div className="border border-black/10 p-4">
                <h3 className="font-heading text-sm uppercase tracking-wider mb-3">Delivery Capability</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-text">Methods:</span>
                    <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.delivery?.delivery_methods?.join(", ")?.replace(/_/g, " ")}</p>
                  </div>
                  <div>
                    <span className="text-muted-text">City Delivery Time:</span>
                    <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.delivery?.city_delivery_time?.replace(/_/g, " ")}</p>
                  </div>
                  <div>
                    <span className="text-muted-text">Outside City:</span>
                    <p className="font-medium">{selectedOnboarding.onboarding_data?.delivery?.delivers_outside_city ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <span className="text-muted-text">Outside Ghana:</span>
                    <p className="font-medium">{selectedOnboarding.onboarding_data?.delivery?.delivers_outside_ghana ? "Yes" : "No"}</p>
                  </div>
                </div>
              </div>

              {/* Quality */}
              <div className="border border-black/10 p-4">
                <h3 className="font-heading text-sm uppercase tracking-wider mb-3">Quality & Source</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-text">Jersey Source:</span>
                    <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.quality?.jersey_source?.replace(/_/g, " ")}</p>
                  </div>
                  <div>
                    <span className="text-muted-text">Materials:</span>
                    <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.quality?.materials?.join(", ")?.replace(/_/g, " ")}</p>
                  </div>
                </div>
              </div>

              <div className="border border-black/10 p-4">
                <h3 className="font-heading text-sm uppercase tracking-wider mb-3">Payout Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-text">Method:</span>
                    <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.payout?.payout_method || "Not provided"}</p>
                  </div>
                  {selectedOnboarding.onboarding_data?.payout?.payout_method === "momo" ? (
                    <>
                      <div>
                        <span className="text-muted-text">MoMo Network:</span>
                        <p className="font-medium capitalize">{selectedOnboarding.onboarding_data?.payout?.momo_network?.replace(/_/g, " ")}</p>
                      </div>
                      <div>
                        <span className="text-muted-text">MoMo Number:</span>
                        <p className="font-medium">{selectedOnboarding.onboarding_data?.payout?.momo_number}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <span className="text-muted-text">Bank Name:</span>
                        <p className="font-medium">{selectedOnboarding.onboarding_data?.payout?.bank_name || "Not provided"}</p>
                      </div>
                      <div>
                        <span className="text-muted-text">Account Name:</span>
                        <p className="font-medium">{selectedOnboarding.onboarding_data?.payout?.account_name || "Not provided"}</p>
                      </div>
                      <div>
                        <span className="text-muted-text">Account Number:</span>
                        <p className="font-medium">{selectedOnboarding.onboarding_data?.payout?.account_number || "Not provided"}</p>
                      </div>
                      <div>
                        <span className="text-muted-text">Bank Branch:</span>
                        <p className="font-medium">{selectedOnboarding.onboarding_data?.payout?.bank_branch || "Not provided"}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Verification Images */}
              <div className="border border-black/10 p-4">
                <h3 className="font-heading text-sm uppercase tracking-wider mb-3">Verification Images</h3>
                <div className="grid grid-cols-3 gap-4">
                  {selectedOnboarding.onboarding_data?.verification?.jersey_photos?.filter(p => p).map((photo, idx) => (
                    <div key={idx} className="aspect-square bg-gray-100 border border-black/10 overflow-hidden">
                      <img 
                        src={photo.startsWith('http') ? photo : `${API}/files/${photo}`}
                        alt={`Jersey ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.src = 'https://via.placeholder.com/200?text=Image'}
                      />
                    </div>
                  ))}
                  {selectedOnboarding.onboarding_data?.verification?.packaging_photo && (
                    <div className="aspect-square bg-gray-100 border border-black/10 overflow-hidden">
                      <img 
                        src={selectedOnboarding.onboarding_data.verification.packaging_photo.startsWith('http') ? selectedOnboarding.onboarding_data.verification.packaging_photo : `${API}/files/${selectedOnboarding.onboarding_data.verification.packaging_photo}`}
                        alt="Packaging"
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.src = 'https://via.placeholder.com/200?text=Image'}
                      />
                    </div>
                  )}
                </div>
                {(!selectedOnboarding.onboarding_data?.verification?.jersey_photos?.some(p => p) && 
                  !selectedOnboarding.onboarding_data?.verification?.packaging_photo) && (
                  <p className="text-muted-text text-sm">No images uploaded</p>
                )}
              </div>

              {/* Commitments */}
              <div className="border border-black/10 p-4">
                <h3 className="font-heading text-sm uppercase tracking-wider mb-3">Commitments</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    {selectedOnboarding.onboarding_data?.commitment?.fulfill_on_time ? 
                      <CheckCircle size={16} className="text-ghana-green" /> : 
                      <XCircle size={16} className="text-ghana-red" />}
                    <span>Fulfill orders on time</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedOnboarding.onboarding_data?.commitment?.fulfill_through_platform ? 
                      <CheckCircle size={16} className="text-ghana-green" /> : 
                      <XCircle size={16} className="text-ghana-red" />}
                    <span>Fulfill through platform</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedOnboarding.onboarding_data?.commitment?.agree_terms ? 
                      <CheckCircle size={16} className="text-ghana-green" /> : 
                      <XCircle size={16} className="text-ghana-red" />}
                    <span>Agreed to terms & conditions</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t border-black/10">
                <Button
                  className="flex-1 bg-ghana-green hover:bg-ghana-green/80"
                  onClick={() => handleApproveVendor(selectedOnboarding.user_id, true)}
                >
                  <CheckCircle size={16} className="mr-2" /> Approve Vendor
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-ghana-red text-ghana-red hover:bg-ghana-red hover:text-white"
                  onClick={() => {
                    const reason = prompt("Enter rejection reason (optional):");
                    handleApproveVendor(selectedOnboarding.user_id, false, reason);
                  }}
                >
                  <XCircle size={16} className="mr-2" /> Reject Application
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
